import React, { useState, useEffect, useRef } from 'react';
import { extractDataFromDocument } from '../services/ocrService';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';
import { ROLE_NAMES } from '../utils';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { cropImageToBlob } from '../utils/imageUtils';

export default function ClientsView({ clients, onClientSelect, dbOps, globalSearch, onOpenNewCase, storage }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({ name: '', idNumber: '', phone: '', email: '', photoUrl: '' });
  const [isOcrLoading, setIsOcrLoading] = useState(false);
  const [croppedPhotoBlob, setCroppedPhotoBlob] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveClient = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    let finalFormData = { ...formData };
    
    if (croppedPhotoBlob) {
      try {
        const photoRef = ref(storage, `client-photos/${finalFormData.idNumber || Date.now()}.jpg`);
        await uploadBytes(photoRef, croppedPhotoBlob);
        const url = await getDownloadURL(photoRef);
        finalFormData.photoUrl = url;
      } catch (err) {
        console.error("Failed to upload photo:", err);
        alert(`שגיאה בהעלאת התמונה: ${err.message || err}`);
      }
    }

    if (editingClient) {
      dbOps.handleSave('clients', finalFormData, editingClient._id);
    } else {
      dbOps.handleSave('clients', { ...finalFormData, totalFee: 0, paid: 0 });
    }
    setIsSaving(false);
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const openNewModal = () => {
    setEditingClient(null);
    setFormData({ name: '', idNumber: '', phone: '', email: '', photoUrl: '' });
    setCroppedPhotoBlob(null);
    setIsModalOpen(true);
  };

  const openEditModal = (client, e) => {
    e.stopPropagation();
    setEditingClient(client);
    setFormData({ name: client.name || '', idNumber: client.idNumber || '', phone: client.phone || '', email: client.email || '', photoUrl: client.photoUrl || '' });
    setCroppedPhotoBlob(null);
    setIsModalOpen(true);
  };

  const handleOcrUpload = async (e) => {
  const file = e.target.files?.[0] || (e.dataTransfer && e.dataTransfer.files?.[0]);
  if (!file) return;
  setIsOcrLoading(true);
  const extractedData = await extractDataFromDocument(file, 'client');

  if (!extractedData.name && !extractedData.idNumber) {
    alert("לא הצלחנו לפענח שדות בצורה אוטומטית.\n\nהטקסט שזוהה (לצורכי בדיקה):\n" + (extractedData.rawText || "לא זוהה טקסט"));
  }

  if (extractedData.faceBoundingBox) {
    try {
      const blob = await cropImageToBlob(file, extractedData.faceBoundingBox);
      setCroppedPhotoBlob(blob);
      delete extractedData.faceBoundingBox;
    } catch (err) {
      console.error("Failed to crop image:", err);
      alert(`שגיאה בחיתוך התמונה: ${err.message || err}`);
    }
  } else {
    // If no face found by OCR
    alert("לא זוהו פנים בתעודה שנסרקה, התמונה לא תישמר.");
  }

  // עדכון הנתונים - הטלפון לא נמצא ב-extractedData ולכן לא ישתנה
  setFormData(prev => ({ ...prev, ...extractedData }));
  setIsOcrLoading(false);
};

  const filteredClients = clients.filter(c => 
    (c.name || '').includes(globalSearch || '') || 
    (c.idNumber || '').includes(globalSearch || '') || 
    (c.phone || '').includes(globalSearch || '') ||
    (c.email || '').includes(globalSearch || '')
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
      <div className="p-4 md:p-6 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between bg-slate-50/50 gap-4">
        <h3 className="font-bold text-lg text-slate-800">ניהול לקוחות</h3>
        <div className="flex gap-2 w-full sm:w-auto">
          <button onClick={() => onOpenNewCase('')} className="flex-1 sm:flex-none bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
             <Briefcase className="w-4 h-4" /> תיק חדש
          </button>
          <button onClick={openNewModal} className="flex-1 sm:flex-none bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
            <Plus className="w-4 h-4" /> לקוח חדש
          </button>
        </div>
      </div>
      <div className="overflow-x-auto flex-1">
        <table className="w-full text-right whitespace-nowrap min-w-[700px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-sm">
            <tr>
              <th className="p-4 font-medium">שם הלקוח</th><th className="p-4 font-medium">ת.ז / ח.פ</th><th className="p-4 font-medium">טלפון</th><th className="p-4 font-medium">דוא"ל</th><th className="p-4 font-medium">שכ"ט כולל</th><th className="p-4 font-medium">שולם</th><th className="p-4 font-medium">יתרה לתשלום</th><th className="p-4 font-medium">פעולות</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {filteredClients.length === 0 ? <tr><td colSpan="8" className="p-8 text-center text-slate-400">לא נמצאו לקוחות מתאימים.</td></tr> :
            filteredClients.map((c) => (
              <tr key={c._id} className="hover:bg-blue-50/30 transition-colors cursor-pointer group" onClick={() => onClientSelect(c)}>
                <td className="p-4 font-semibold text-blue-700">
                  <div className="flex items-center gap-3">
                    {c.photoUrl ? (
                      <img src={c.photoUrl} alt={c.name} className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                        <User className="w-4 h-4" />
                      </div>
                    )}
                    <span className="hover:underline">{c.name}</span>
                  </div>
                </td>
                <td className="p-4 text-slate-600">{c.idNumber}</td>
                <td className="p-4 text-slate-600" dir="ltr">{c.phone}</td>
                <td className="p-4 text-slate-600">{c.email}</td>
                <td className="p-4 text-slate-800 font-medium">₪{c.totalFee?.toLocaleString()}</td>
                <td className="p-4 text-emerald-600 font-medium">₪{c.paid?.toLocaleString()}</td>
                <td className="p-4 text-red-600 font-medium">₪{((c.totalFee || 0) - (c.paid || 0)).toLocaleString()}</td>
                <td className="p-4 flex items-center gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => openEditModal(c, e)} className="text-slate-400 hover:text-blue-600 transition-colors p-1" title="ערוך לקוח"><Edit className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); dbOps.handleDelete('clients', c._id); }} className="text-slate-400 hover:text-red-600 transition-colors p-1" title="מחק לקוח"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 m-0" dir="rtl">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-slate-800 flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> {editingClient ? 'עריכת פרטי לקוח' : 'הוספת לקוח חדש'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="flex flex-col md:flex-row">
              {/* אזור גרירת מסמך לזיהוי אוטומטי */}
              {!editingClient && (
                <div className="w-full md:w-1/3 bg-slate-50 p-6 border-b md:border-b-0 md:border-l border-slate-200 flex flex-col items-center justify-center">
                  <div className="w-full h-48 md:h-full border-2 border-dashed border-blue-300 rounded-xl flex flex-col items-center justify-center bg-white hover:bg-blue-50 transition-colors relative cursor-pointer group"
                       onDragOver={(e) => e.preventDefault()}
                       onDrop={(e) => { e.preventDefault(); handleOcrUpload(e); }}>
                    {isOcrLoading ? (
                      <div className="flex flex-col items-center">
                        <RefreshCw className="w-10 h-10 text-blue-500 animate-spin mb-3"/>
                        <p className="text-sm font-medium text-slate-700">סורק ומפענח נתונים...</p>
                      </div>
                    ) : (
                      <>
                        <Scan className="w-10 h-10 text-blue-400 group-hover:text-blue-600 transition-colors mb-3" />
                        <h4 className="font-bold text-slate-800 text-sm mb-1">סריקת תעודת זהות</h4>
                        <p className="text-xs text-slate-500 text-center px-4 mb-4">גרור לכאן תמונה או לחץ לבחירה כדי למלא את פרטי הלקוח אוטומטית</p>
                        <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={handleOcrUpload} accept="image/*,.pdf" />
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* טופס הזנת הנתונים */}
              <form onSubmit={handleSaveClient} className="p-4 md:p-6 space-y-4 flex-1">
                
                {/* תצוגת תמונת פרופיל */}
                <div className="flex items-center gap-4 mb-4">
                  {(croppedPhotoBlob || formData.photoUrl) ? (
                    <img 
                      src={croppedPhotoBlob ? URL.createObjectURL(croppedPhotoBlob) : formData.photoUrl} 
                      alt="Client Profile" 
                      className="w-16 h-16 rounded-full object-cover border-2 border-blue-100 shadow-sm" 
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-200">
                      <User className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-bold text-slate-700">תמונת לקוח</h4>
                    <p className="text-xs text-slate-500 mb-2">{(croppedPhotoBlob || formData.photoUrl) ? "תמונה קיימת / נבחרה ותישמר בכרטסת" : "תמונה לא קיימת"}</p>
                    <label className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer font-medium flex items-center gap-1 bg-blue-50 hover:bg-blue-100 transition-colors px-2 py-1 rounded w-fit">
                      <Edit className="w-3 h-3" /> העלה או החלף תמונה
                      <input type="file" className="hidden" accept="image/*" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCroppedPhotoBlob(file);
                        }
                      }} />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">שם מלא / שם חברה <span className="text-red-500">*</span></label>
                  <input required type="text" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">ת.ז / ח.פ</label>
                  <input type="text" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.idNumber} onChange={e => setFormData({...formData, idNumber: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">מספר טלפון</label>
                  <input type="tel" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">דואר אלקטרוני</label>
                  <input type="email" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
                
                <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 mt-6">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" disabled={isSaving}>ביטול</button>
                  <button type="submit" disabled={isSaving} className="px-4 py-2.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2">
                    {isSaving ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} 
                    {isSaving ? 'שומר ומעלה תמונה...' : 'שמור לקוח'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
